import i18next from 'i18next';
import en from '../../locales/en.json';
import nl from '../../locales/nl.json';
import fr from '../../locales/fr.json';
import de from '../../locales/de.json';

import { applyI18n } from './dom';

const STORAGE_KEY = 'shareweb:locale';
const localeListeners = new Set<() => void>();

export async function initI18n(): Promise<void> {
  const stored = localStorage.getItem(STORAGE_KEY);
  const browserLang = navigator.language.slice(0, 2);
  const fallback = ['nl', 'en', 'fr', 'de'].includes(browserLang) ? browserLang : 'en';

  await i18next.init({
    lng: stored ?? fallback,
    fallbackLng: 'en',
    showSupportNotice: false,
    resources: {
      en: { translation: en },
      nl: { translation: nl },
      fr: { translation: fr },
      de: { translation: de },
    },
  });

  i18next.on('languageChanged', () => {
    applyI18n(document);
    localeListeners.forEach((listener) => listener());
  });
}

export function onLocaleChange(listener: () => void): () => void {
  localeListeners.add(listener);
  return () => localeListeners.delete(listener);
}

const SUPPORTED_LOCALES = new Set(['en', 'nl', 'fr', 'de']);

export function normalizeLocale(locale: string | null | undefined): string {
  const value = (locale ?? '').trim().slice(0, 2).toLowerCase();
  return SUPPORTED_LOCALES.has(value) ? value : 'en';
}

export async function setLocale(
  locale: string,
  options?: { persistRemote?: boolean },
): Promise<void> {
  const normalized = normalizeLocale(locale);
  localStorage.setItem(STORAGE_KEY, normalized);
  await i18next.changeLanguage(normalized);

  if (options?.persistRemote === false) {
    return;
  }

  try {
    const { api } = await import('../api/client');
    await api.patchMyLocale(normalized);
  } catch (cause) {
    // Guests / anonymous: localStorage only; ignore unauthorized.
    if (cause && typeof cause === 'object' && 'status' in cause && (cause as { status: number }).status === 401) {
      return;
    }
    console.error('Persist locale failed', cause);
  }
}

/** Apply server/session locale preference (writes localStorage, skips remote PATCH). */
export async function applyPreferredLocale(locale: string | null | undefined): Promise<void> {
  if (!locale) {
    return;
  }
  const normalized = normalizeLocale(locale);
  if (normalized === i18next.language?.slice(0, 2)) {
    localStorage.setItem(STORAGE_KEY, normalized);
    return;
  }
  await setLocale(normalized, { persistRemote: false });
}

export { applyI18n };

export function t(key: string, options?: Record<string, unknown>): string {
  return i18next.t(key, options);
}

export { i18next };
